import { Button, Card, Field, Input, Stack, Text, Textarea } from '@chakra-ui/react';
import { leadStore } from '../../operations/leadStore';
import { toaster } from '../../components/ui/toaster';
import { useDatabase } from '../../hooks/useDatabase';
import { useState } from 'react';

const EMAIL_REGEX = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const PHONE_REGEX = /^(?:(?:(?:(?:\+|00)\d{2})?[ -]?(?:(?:\(0?\d{2}\))|(?:0?\d{2})))?[ -]?(?:\d{3}[- ]?\d{2}[- ]?\d{2}|\d{2}[- ]?\d{2}[- ]?\d{3}|\d{7})|(?:(?:(?:\+|00)\d{2})?[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}))$/;

const isValidEmail = (value: string) => value.trim().length > 0 && EMAIL_REGEX.test(value.trim());
const isValidPhone = (value: string) => value.trim().length > 0 && PHONE_REGEX.test(value.trim());

type EnrollmentLeadFormProps = {
  variant?: 'default' | 'plain';
};

export const EnrollmentLeadForm = ({ variant = 'default' }: EnrollmentLeadFormProps) => {
  const database = useDatabase();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const phoneError = phone.trim().length > 0 && !isValidPhone(phone) ? 'Podaj poprawny numer telefonu.' : '';
  const emailError = email.trim().length > 0 && !isValidEmail(email) ? 'Podaj poprawny adres email.' : '';
  const isSubmitEnabled = name.trim().length > 0 && (isValidPhone(phone) || isValidEmail(email));

  const handleSubmit = async () => {
    if (!isSubmitEnabled) return;

    setIsSubmitting(true);
    try {
      await leadStore(database, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        notes: notes.trim(),
      });

      toaster.create({
        title: 'Dziękujemy!',
        description: 'Twoja wiadomość została zapisana. Skontaktujemy się z Tobą, gdy pojawią się dostępne terminy.',
        type: 'success',
        duration: 4000,
      });

      setName('');
      setPhone('');
      setEmail('');
      setNotes('');
    } catch (error) {
      console.error('Failed to store enrollment lead', error);
      toaster.create({
        title: 'Nie udało się wysłać formularza',
        description: 'Spróbuj ponownie za chwilę.',
        type: 'error',
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const promptText =
    variant === 'plain'
      ? 'Podaj swój adres email lub numer telefonu, a skontaktujemy się z Tobą, gdy tylko pojawią się pierwsze terminy.'
      : 'Podaj swój adres email lub numer telefonu, a skontaktujemy się z Tobą, gdy pojawią się nowe terminy.';

  const isPlain = variant === 'plain';

  return (
    <Card.Root
      w="full"
      mx="auto"
      borderWidth={isPlain ? '0' : '1px'}
      borderColor={isPlain ? 'transparent' : 'orange.200'}
      bg={isPlain ? 'transparent' : 'white'}
      shadow={isPlain ? 'none' : 'sm'}
    >
      <Card.Body p={isPlain ? 2 : 5}>
        <Stack gap={3}>
          <Text fontSize="sm" color="gray.700" fontWeight="medium" fontStyle={isPlain ? 'italic' : 'normal'} textAlign="center">
            {promptText}
          </Text>

          <Field.Root>
            <Field.Label>Imię</Field.Label>
            <Input
              aria-label="Imię i nazwisko"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field.Root>

          <Field.Root invalid={Boolean(phoneError)}>
            <Field.Label>Telefon</Field.Label>
            <Input
              aria-label="Telefon"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            {phoneError && <Field.ErrorText>{phoneError}</Field.ErrorText>}
          </Field.Root>

          <Field.Root invalid={Boolean(emailError)}>
            <Field.Label>Email</Field.Label>
            <Input
              aria-label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            {emailError && <Field.ErrorText>{emailError}</Field.ErrorText>}
          </Field.Root>

          <Field.Root>
            <Field.Label>Dodatkowe informacje</Field.Label>
            <Textarea
              aria-label="Dodatkowe informacje"
              value={notes}
              rows={5}
              onChange={(event) => setNotes(event.target.value)}
              resize="vertical"
            />
          </Field.Root>

          <Button
            type="button"
            colorPalette="orange"
            onClick={handleSubmit}
            disabled={!isSubmitEnabled || isSubmitting}
            loading={isSubmitting}
          >
            Wyślij
          </Button>
        </Stack>
      </Card.Body>
    </Card.Root>
  );
};
